import React, { useState, Fragment } from 'react';
import { Dialog, DialogPanel, DialogTitle, Transition, TransitionChild } from '@headlessui/react';
import { BuildingOffice2Icon, CheckCircleIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { ArrowsRightLeftIcon } from '@heroicons/react/20/solid';

export default function ProfileOrgSwitcherModal({ organizations, currentOrganizationId, switchUrl }) {
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const handleSwitch = (orgId) => {
    if (orgId === currentOrganizationId) {
      setIsOpen(false);
      return;
    }
    setIsLoading(true);
    window.location.href = `${switchUrl}?organization_id=${orgId}`;
  };

  const currentOrg = organizations.find(org => org.id === currentOrganizationId);
  const otherOrgs = organizations.filter(org => org.id !== currentOrganizationId);

  return (
    <>
      {/* Switch Button */}
      <button
        type="button"
        onClick={() => setIsOpen(true)}
        className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
      >
        <ArrowsRightLeftIcon className="w-3.5 h-3.5" />
        <span className="hidden sm:inline">Switch</span>
      </button>

      {/* Modal */}
      <Transition show={isOpen} as={Fragment}>
        <Dialog as="div" className="relative z-50" onClose={() => setIsOpen(false)}>
          <TransitionChild
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-gray-500/75 backdrop-blur-sm" />
          </TransitionChild>

          <div className="fixed inset-0 z-10 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4">
              <TransitionChild
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 scale-95"
                enterTo="opacity-100 scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 scale-100"
                leaveTo="opacity-0 scale-95"
              >
                <DialogPanel className="relative w-full max-w-md transform rounded-2xl bg-white shadow-2xl transition-all">
                  {/* Header */}
                  <div className="px-6 py-4 border-b border-gray-100">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-lg bg-indigo-100 flex items-center justify-center">
                          <BuildingOffice2Icon className="w-5 h-5 text-indigo-600" />
                        </div>
                        <div>
                          <DialogTitle className="text-base font-semibold text-gray-900">
                            Switch Organization
                          </DialogTitle>
                          <p className="text-sm text-gray-500">
                            Select which union to view
                          </p>
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={() => setIsOpen(false)}
                        className="p-2 text-gray-400 hover:text-gray-500 hover:bg-gray-100 rounded-lg transition-colors"
                      >
                        <XMarkIcon className="w-5 h-5" />
                      </button>
                    </div>
                  </div>

                  {/* Organization List */}
                  <div className="p-4 space-y-2 max-h-96 overflow-y-auto">
                    {/* Current Organization */}
                    {currentOrg && (
                      <div className="mb-4">
                        <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2 px-2">
                          Current
                        </p>
                        <div className="p-4 rounded-xl border-2 border-indigo-300 bg-indigo-50">
                          <div className="flex items-center gap-3">
                            {currentOrg.logo_url ? (
                              <img
                                src={currentOrg.logo_url}
                                alt={currentOrg.name}
                                className="w-12 h-12 rounded-lg object-cover"
                              />
                            ) : (
                              <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center">
                                <span className="text-sm font-bold text-white">
                                  {currentOrg.initials}
                                </span>
                              </div>
                            )}
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-semibold text-gray-900 truncate">
                                {currentOrg.name}
                              </p>
                              <p className="text-xs text-indigo-600 font-medium">
                                Currently viewing
                              </p>
                            </div>
                            <CheckCircleIcon className="w-6 h-6 text-indigo-600 flex-shrink-0" />
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Other Organizations */}
                    {otherOrgs.length > 0 && (
                      <div>
                        <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2 px-2">
                          Other Organizations
                        </p>
                        <div className="space-y-2">
                          {otherOrgs.map((org) => (
                            <button
                              key={org.id}
                              type="button"
                              onClick={() => handleSwitch(org.id)}
                              disabled={isLoading}
                              className="w-full p-4 rounded-xl border-2 border-gray-200 bg-white hover:border-indigo-300 hover:bg-indigo-50 transition-all text-left group disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                              <div className="flex items-center gap-3">
                                {org.logo_url ? (
                                  <img
                                    src={org.logo_url}
                                    alt={org.name}
                                    className="w-12 h-12 rounded-lg object-cover"
                                  />
                                ) : (
                                  <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-gray-400 to-gray-600 group-hover:from-indigo-500 group-hover:to-purple-600 flex items-center justify-center transition-colors">
                                    <span className="text-sm font-bold text-white">
                                      {org.initials}
                                    </span>
                                  </div>
                                )}
                                <div className="flex-1 min-w-0">
                                  <p className="text-sm font-semibold text-gray-900 truncate">
                                    {org.name}
                                  </p>
                                  <p className="text-xs text-gray-500 group-hover:text-indigo-600 transition-colors">
                                    Click to switch
                                  </p>
                                </div>
                                <svg
                                  className="w-5 h-5 text-gray-400 group-hover:text-indigo-500 transition-colors"
                                  fill="none"
                                  stroke="currentColor"
                                  viewBox="0 0 24 24"
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M9 5l7 7-7 7"
                                  />
                                </svg>
                              </div>
                            </button>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Footer */}
                  <div className="px-6 py-4 border-t border-gray-100 bg-gray-50 rounded-b-2xl">
                    <p className="text-xs text-gray-500 text-center">
                      You're a member of {organizations.length} organization{organizations.length !== 1 ? 's' : ''}
                    </p>
                  </div>
                </DialogPanel>
              </TransitionChild>
            </div>
          </div>
        </Dialog>
      </Transition>
    </>
  );
}

